import React, { FC, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import JourneySlideover from 'src/common/JourneySlideOver';
import { JourneyWorkspaceSelect } from 'src/dashboard/components/slide-over/create-journey.slideover';
import useDashboardStore, { DashboardState } from 'src/dashboard/store/dashboard';
import { useNewJourneyStore } from 'src/dashboard/store/new-journey';
import {
  CompanyInfo,
  RecipientSearchInput,
} from 'src/editor/content-creation/components/dashboard/recipient-search-input';
import { useProspectLogo } from 'src/common/hooks/use-prospect-logo.hook';
import { Nullable } from 'src/types/nullable.type';
import { animated, useSpring, useSpringRef } from '@react-spring/web';
import LoadingSpinnerLight from 'src/common/components/loading-spinner/light';
import { useUpdateEffect } from 'react-use';
import { FLOAT_ANIMATION_CONFIG } from 'src/dashboard/create-journey/duplicate';

const isCreatingJourneyFromTemplateSelector = (state: DashboardState) => state.isCreatingJourneyFromTemplate;

export const DashboardHomeOrganizationTemplatesNewJourneySlideover: FC = () => {
  const setIsCreatingJourneyFromTemplate = useDashboardStore((state) => state.setIsCreatingJourneyFromTemplate);
  const isCreatingJourneyFromTemplate = useDashboardStore(isCreatingJourneyFromTemplateSelector);
  const [createInProgress, setCreateInProgress] = useState(false);

  const createJourney = useNewJourneyStore((state) => state.createJourney);
  const setJourneyRecipient = useNewJourneyStore((state) => state.setJourneyRecipient);
  const resetStore = useNewJourneyStore((state) => state.resetStore);

  const history = useHistory();

  const recipientInputElementRef = useRef<HTMLInputElement>(null);
  const { fetchProspectLogo, logoUrl, fetching, setLogoUrl, setGeneratedSlug, setDomain } = useProspectLogo();
  const [selectedRecipient, setSelectedRecipient] = useState<Nullable<CompanyInfo>>(null);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const recipientLogoAnimationApi = useSpringRef();
  const [recipientLogoAnimationStyle] = useSpring(() => ({
    ref: recipientLogoAnimationApi,
    from: { opacity: 0 },
    to: { opacity: 1 },
  }));

  const onResetRecipient = () => {
    setLogoUrl(null);
    setDomain(null);
    setGeneratedSlug(null);
  };

   const onSelectRecipient = (recipient: CompanyInfo) => {
    setSelectedRecipient(recipient);
    if (recipient) {
      fetchProspectLogo(recipient.domain);
      setJourneyRecipient(recipient);
    }

  };

   const renderIconWithLoader = (url?: Nullable<string>) => {
    return (
      <animated.div
        style={recipientLogoAnimationStyle}
        className='bg-neue-journey-fg-10 rounded-lg p-2 w-10 h-10 flex-shrink-0 flex items-center justify-center'
      >
        {!url ? <LoadingSpinnerLight /> : <img src={url} className='h-6 w-auto' alt='' />}
      </animated.div>
    );
  };

   useUpdateEffect(() => {
    if (!recipientSearchQuery) {
      recipientLogoAnimationApi.start({ opacity: 0, config: FLOAT_ANIMATION_CONFIG });
      return;
    }
    if (fetching) {
      recipientLogoAnimationApi.start({ opacity: 1, config: FLOAT_ANIMATION_CONFIG });
    }
  }, [fetching, recipientSearchQuery]);

  const onClose = () => {
    setIsCreatingJourneyFromTemplate(false);
    resetStore();
  };

  const onCreateClick = async () => {
    setCreateInProgress(true);
    await createJourney(history);
    setIsCreatingJourneyFromTemplate(false);
    setCreateInProgress(false);
  };

  return (
    <JourneySlideover
      title={`Create new journey from template`}
      open={isCreatingJourneyFromTemplate}
      maxWidth='xs'
      onClose={onClose}
      isBedrock={true}
    >
      <div className='flex flex-col space-y-4'>
        <JourneyWorkspaceSelect />
        <div className='flex flex-col gap-4'>
          <p className='text-neue-journey-large'>And now pick a new recipient  (optional)</p>
          <div className='flex flex-row shrink-0 gap-2 relative'>
            <RecipientSearchInput
              groupClasses="border-2 border-bedrock-gray-medium rounded-lg"
              groupOptions="mb-44"
              inputClassName='pr-0 flex-1'
              inputRef={recipientInputElementRef}
              placeholder="Enter recipient's website URL"
              onResetRecipient={onResetRecipient}
              onSelectRecipient={onSelectRecipient}
              onRecipientSearchQueryChange={setRecipientSearchQuery}
              hasSelectedRecipient={!!selectedRecipient}
              optionsContainerClassName='bg-neue-journey-bg-90'
              optionClassName='bg-neue-journey-bg-90 transition-colors hover:bg-neue-journey-fg-10'
            />
            {renderIconWithLoader(logoUrl)}
          </div>
          <button
            type='button'
            className='btn-bedrock-primary w-full'
            onClick={onCreateClick}
            disabled={createInProgress}
          >
            Create Journey
          </button>
        </div>
      </div>
    </JourneySlideover>
  );
};
